import {useRef, useState, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {DeskProps} from './types'
import {DeskStatusColor, DeskStatusID} from './enums'
import {Group} from './components'
import {RootStore} from '../../../redux/store'
import {getUserInitials} from '../utils/utils'
import localCss from '../booking.module.css'
import {
  calculateAccessPosition,
  deskColour,
  getLabelCoords
} from './deskRectangleLogic'
import {BookingWizardSteps} from '../BlockBooking/enums'
import {BookingAvailability, BookingAvailabilityColors} from '../consts'
import {DeskFeature} from '../../../services/booking/types'
import {calculateRotationAngle} from '../Designer/Utils/toolbarUtils'

import {getDeskNameWithRow} from '../bookingLogic'

function DeskRectangle({
  featureDetail,
  existingBookings,
 
  isActive,
  availability,
  isBlockBooking,
  onActionMouseEnter
}: DeskProps) {
  const objectRef = useRef<SVGGElement | null>(null)
  const {employeeDetails} = useSelector((state: RootStore) => state.appSettings)

  const {
    selectedFeature,
    selectedAlternativeFeatureID,
    currentStep: currentWizardStep
  } = useSelector((state: RootStore) => state.deskBookingWizard)
  
  const {foundDeskFeatureId, foundDeskTimestamp, foundDeskName} = useSelector(
    (state: RootStore) => state.deskBooking
  )
  
  const deskFeature = featureDetail as DeskFeature
  
  // State to track if this desk is currently found and flashing
  const [isFoundDesk, setIsFoundDesk] = useState(false)
  const [showPersistentRed, setShowPersistentRed] = useState(false)
  
  // Effect to handle found desk highlighting
  useEffect(() => {
    const deskNameWithRow = getDeskNameWithRow(featureDetail);

    function norm(name: string | undefined | null) {
      return (name || '').toLowerCase().trim();
    }

    const highlightThisDesk = foundDeskFeatureId
      ? foundDeskFeatureId === featureDetail.id
      : (!!foundDeskName && norm(deskNameWithRow) === norm(foundDeskName));

    if (highlightThisDesk && foundDeskTimestamp) {
      setIsFoundDesk(true)
      setShowPersistentRed(false)

      const persistentTimer = setTimeout(() => {
        setIsFoundDesk(false)
        setShowPersistentRed(true)
      }, 2000)

      return () => clearTimeout(persistentTimer)
    }

    if (!highlightThisDesk) {
      setIsFoundDesk(false)
      setShowPersistentRed(false)
    }
  }, [foundDeskFeatureId, foundDeskName, foundDeskTimestamp, featureDetail.id, deskFeature.label, featureDetail])

  if (deskFeature.rotationAngle === undefined || deskFeature.rotationAngle === 0) {
    deskFeature.rotationAngle = calculateRotationAngle(deskFeature)
  }

  const {submissionsInProgress} = useSelector((state: RootStore) => state.deskBookingStatus)

  // NOTE: No provision has yet been made for more than 1 port    
  const PORT_BORDER_RADIUS = featureDetail.ports?.length
    ? Number(featureDetail.ports?.[0].radius) * 1.5
    : 0

  const isOwnBooking = () => !!existingBookings?.find(booking => booking.employeeId === employeeDetails.employeeId)

  const derivedPosition = calculateAccessPosition(featureDetail, PORT_BORDER_RADIUS)

  const rotationCenterX = featureDetail.width / 2
  const rotationCenterY = featureDetail.height / 2

  const getPortPosition = (): { x: number; y: number } => {
    if (deskFeature.rotationAngle === 0) {
      // Call calculateAccessPosition with both required arguments  
      const accessPosition = calculateAccessPosition(featureDetail, PORT_BORDER_RADIUS)

      // Adjust x and y to be relative to the Group's coordinate system  
      const x = accessPosition.x - featureDetail.x
      const y = accessPosition.y - featureDetail.y

      return {x, y}
    }
    // Existing position calculation when rotationAngle is defined  
    const {width} = featureDetail
    const {height} = featureDetail
    const centerY = height / 2

    const portRadius = featureDetail.ports?.[0]?.radius || 0
    const portOverlapOffset = portRadius / 2

    const x = width + portOverlapOffset
    const y = centerY

    return {x, y}

  }

  const position = getPortPosition()

  const labelGlobalX = featureDetail.x + featureDetail.width / 2
  const labelGlobalY = featureDetail.y + featureDetail.height / 2
  const textAnchor = 'middle'

  const labelCoords = getLabelCoords(featureDetail, derivedPosition.position)

  const isBlockBookingAndNotSelectedFeature = () =>
    isBlockBooking && !selectedFeature
  const isBlockBookingAndSelectedFeatureMatch = () =>
    isBlockBooking && selectedFeature?.id === featureDetail.id
  const isOwnBookingDuringBlockBookingWizard = () =>
    isOwnBooking() && currentWizardStep === BookingWizardSteps.STEP_2_SELECT_DESK
  const availabilityColor = availability.color

  const getDeskFillColor = () => {
    if (
      isBlockBookingAndNotSelectedFeature() ||
      isBlockBookingAndSelectedFeatureMatch() ||
      isOwnBookingDuringBlockBookingWizard()
    ) {
      return DeskStatusColor.ACTIVE
    }

    return deskColour(featureDetail, isOwnBooking())
  }

  const shouldHighlightDesk = () => {
    if (
      isBlockBooking &&
      currentWizardStep === BookingWizardSteps.STEP_2_SELECT_DESK &&
      selectedFeature?.id === featureDetail.id
    ) {
      return true
    }
    if (isBlockBooking && currentWizardStep === BookingWizardSteps.STEP_4_ALTERNATIVES) {
      if (selectedAlternativeFeatureID === featureDetail.id) {
        return true
      }
    }
    return false
  }

  const submissionInProgress = () =>
    submissionsInProgress.some(s =>
      s === featureDetail.id
    )

  const featureDetailLocationId = () =>
    featureDetail?.locationId === 1

  const getGlobalRotatedPoint = (localX: number, localY: number) => {
    const angleRad = (deskFeature.rotationAngle || 0) * (Math.PI / 180)
    const dx = localX - rotationCenterX
    const dy = localY - rotationCenterY

    const rotatedDx = dx * Math.cos(angleRad) - dy * Math.sin(angleRad)
    const rotatedDy = dx * Math.sin(angleRad) + dy * Math.cos(angleRad)

    return {
      x: featureDetail.x + rotationCenterX + rotatedDx,
      y: featureDetail.y + rotationCenterY + rotatedDy
    }
  }

  const globalRotatedPos = getGlobalRotatedPoint(position.x, position.y)

  const globalTextX = globalRotatedPos.x
  const globalTextY = globalRotatedPos.y

  // Use 0 offsets as the point is now correctly rotated to the port center
  const textOffsetX = 0
  const textOffsetY = 0
  const svgOffsetX = 0
  const svgOffsetY = 0

  return (
    <>
      <Group
        id={`desk_${featureDetail.id}`}
        ref={objectRef}
        style={{ overflow: 'visible', cursor: 'pointer' }}
        opacity={submissionInProgress() ? 0.5 : 1}
        onClick={e => {
          onActionMouseEnter?.(
            e.currentTarget,
            availability,
            isOwnBooking() || false,

            existingBookings
          )
        }}
        highlightColor={isOwnBooking() ? DeskStatusColor.OWN_BOOKING : availabilityColor}
        transform={`translate(${featureDetail.x}, ${featureDetail.y}) rotate(${deskFeature.rotationAngle}, ${rotationCenterX}, ${rotationCenterY})`}
      >
        <rect
          y={0}
          x={0}
          width={featureDetail.width}
          height={featureDetail.height}
          rx={featureDetail.borderRadius}
          fill={getDeskFillColor()}
          className={isActive ? '' : localCss.bookingZoneUnfocussedDesk}
          cursor="pointer"
        />
        {shouldHighlightDesk() && !isFoundDesk && !showPersistentRed && (
          <rect
            y={0}
            x={0}
            width={featureDetail.width}
            height={featureDetail.height}
            rx={featureDetail.borderRadius}
            fill={getDeskFillColor()}
            strokeWidth={4}
            stroke="#7D7D7D"
            cursor="pointer"
          />
        )}
        {(isFoundDesk || showPersistentRed) && (
          <rect
            y={0}
            x={0}
            width={featureDetail.width}
            height={featureDetail.height}
            rx={featureDetail.borderRadius}
            fill={getDeskFillColor()}
            className={isFoundDesk ? localCss.foundDeskFlash : localCss.foundDeskPersistent}
            cursor="pointer"
          />
        )}

        {featureDetail.statusId === DeskStatusID.VACANT && Boolean(featureDetail.ports?.length) && (
          <g>
            <circle cx={position.x} cy={position.y} r={PORT_BORDER_RADIUS} fill="white" />
            <circle cx={position.x} cy={position.y} r={PORT_BORDER_RADIUS - 6} fill="white" />
            <circle // without the key being associated with color, the circle sometimes does not re-render
              // when the color changes
              key={`circle-${availabilityColor}`}
              cx={position.x}
              cy={position.y}
              r={featureDetail.ports?.[0].radius}
              fill={isOwnBooking() ? DeskStatusColor.OWN_BOOKING : availabilityColor}
              cursor={availabilityColor === BookingAvailabilityColors.NOT_ACTIVE ? '' : 'pointer'}
              className={isActive ? '' : localCss.bookingZoneUnfocussedDesk}
            />
          </g>
        )}
      </Group>
      {/* Render the label outside the transformed group to keep it upright */}
      {featureDetail.label && (
        <text
          x={labelGlobalX}
          y={labelGlobalY}
          fontFamily="Poppins"
          fontSize="19px"
          fontWeight="bold"
          fill="white"
          style={{ userSelect: 'none', pointerEvents: 'none' }}
          textAnchor={textAnchor}
          alignmentBaseline="middle"
        >
          {featureDetail.label}
        </text>
      )}
      {/* Render animated circles and pin together in single group when hopping */}
      {featureDetail.statusId === DeskStatusID.VACANT &&
        Boolean(featureDetail.ports?.length) &&
        (isFoundDesk || showPersistentRed) &&
        !isOwnBooking() &&
        availability !== BookingAvailability.AVAILABLE && (
          <g className={localCss.initialsHop}>
            <circle
              cx={globalTextX + textOffsetX}
              cy={globalTextY + textOffsetY}
              r={PORT_BORDER_RADIUS}
              fill="white"
              style={{ pointerEvents: 'none' }}
            />
            <circle
              cx={globalTextX + textOffsetX}
              cy={globalTextY + textOffsetY}
              r={PORT_BORDER_RADIUS - 6}
              fill="white"
              style={{ pointerEvents: 'none' }}
            />
            <circle
              key={`circle-hop-${availabilityColor}`}
              cx={globalTextX + textOffsetX}
              cy={globalTextY + textOffsetY}
              r={featureDetail.ports?.[0].radius}
              fill={BookingAvailabilityColors.NOT_AVAILABLE}
              style={{ pointerEvents: 'none' }}
            />
            <svg
              data-testid="found-desk-pin"
              xmlns="http://www.w3.org/2000/svg"
              width={featureDetailLocationId() ? '16' : '14'}
              height={featureDetailLocationId() ? '24' : '20'}
              viewBox="0 0 16.991 24.567"
              x={globalTextX + svgOffsetX - (featureDetailLocationId() ? 8 : 7)}
              y={globalTextY + svgOffsetY - (featureDetailLocationId() ? 12 : 10)}
              style={{ pointerEvents: 'none' }}
            >
              <path
                data-name="Path 2426"
                d="M8.5,0A8.541,8.541,0,0,0,0,8.6c0,6.449,8.5,15.968,8.5,15.968s8.5-9.52,8.5-15.968A8.541,8.541,0,0,0,8.5,0m0,11.669A3.071,3.071,0,1,1,11.53,8.6,3.054,3.054,0,0,1,8.5,11.669"
                fill="#fff"
              />
            </svg>
          </g>
        )}
      {/* Render the text and svg outside the group */}
      {featureDetail.statusId === DeskStatusID.VACANT && Boolean(featureDetail.ports?.length) && (
        <>
          {!isOwnBooking() &&
            !(isFoundDesk || showPersistentRed) &&
            availability !== BookingAvailability.AVAILABLE &&
            Number(existingBookings?.length) > 0 && (
              <text
                x={globalTextX + textOffsetX}
                y={globalTextY + textOffsetY}
                textAnchor="middle"
                fill="white"
                alignmentBaseline="middle"
                fontFamily="Poppins"
                fontSize="13px"
                cursor={availabilityColor === BookingAvailabilityColors.NOT_ACTIVE ? '' : 'pointer'}
                data-testid="booking-initials"
                style={{ pointerEvents: 'none' }}
              >
                {isBlockBooking ? '' : getUserInitials(existingBookings?.[0].displayName || '')}
              </text>
            )}

          {!isBlockBooking && isOwnBooking() && (
            <svg
              data-testid="own-booking-pin"
              xmlns="http://www.w3.org/2000/svg"
              width={featureDetailLocationId() ? '16' : '14'}
              height={featureDetailLocationId() ? '24' : '20'}
              viewBox="0 0 16.991 24.567"
              x={globalTextX + svgOffsetX - (featureDetailLocationId() ? 8 : 7)}
              y={globalTextY + svgOffsetY - (featureDetailLocationId() ? 12 : 10)}
              style={{ pointerEvents: 'none' }}
            >
              <path
                data-name="Path 2426"
                d="M8.5,0A8.541,8.541,0,0,0,0,8.6c0,6.449,8.5,15.968,8.5,15.968s8.5-9.52,8.5-15.968A8.541,8.541,0,0,0,8.5,0m0,11.669A3.071,3.071,0,1,1,11.53,8.6,3.054,3.054,0,0,1,8.5,11.669"
                fill="#fff"
              />
            </svg>
          )}
        </>
      )}
    </>
  )
}

export default DeskRectangle
